import { Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  batchEditSupportRole,
  deleteSupportUser,
  saveSupportRoleUsers,
} from '../../../../api/admin/rbac';
import {
  CommonButton,
  CommonButtonGroup,
  MainButton,
} from '../../../../components/button/main';

import { messageError } from '../../../../components/message';
import { ModalWrap } from '../../../../components/modal-wrap';
import { PaginationTable } from '../../../../components/pagination-table';

import { CommonTag } from '../../../../components/tag';
import { getConfig } from '../../../../config';
import getFormatMessage from '../../../../locales/get-format-message';
import { AppLoginPlatform, DeployArea, RbacRoleType } from '../../../../model';
import {
  IApiAdminRbacRole,
  ISupportUserModel,
  IUserListItem,
} from '../../../../model/api-modules';
import { IRootState } from '../../../../store/reducers';
import { SvgIcon } from '../../../component/svg-icon';
import { SvgIconName } from '../../../component/svg-icon/model';
import { AdminPermCheck } from '../../components/admin-permission-check';
import { ResetPasswordModal } from '../../components/reset-password';
import { getUserNameByEmail } from '../../lib/columns';
import { InviteDrawer } from './drawer';
import { EditUserDrawer } from './helper';
import styles from './index.module.scss';

export interface IAdminMemberItem {
  userName: string;
  email: string;
  roles: readonly { name: string; type: RbacRoleType }[];
  inviter?: string;
}

interface IProps {
  readonly loading: boolean;
  allUser: readonly IUserListItem[];
  roles: readonly IApiAdminRbacRole[];
  members: readonly ISupportUserModel[];
  onChange: () => void;
}

export const AdminMembers = (props: IProps) => {
  const { allUser, roles, members: supportMembers, onChange, loading } = props;
  const [showInviteDrawer, setShowInviteDrawer] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<IAdminMemberItem>();
  const [members, setMembers] = useState<readonly IAdminMemberItem[]>([]);
  const [showResetPassport, setShowResetPassport] = useState<boolean>(false);
  const [resetPassportMember, setResetPassportMember] =
    useState<IAdminMemberItem>();
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  useEffect(() => {
    init();
  }, [roles, allUser, supportMembers]);

  const init = async () => {
    if (!roles) {
      return;
    }

    const formatUsers = supportMembers
      .map((item) => {
        const userId = item.UserID;
        return {
          userName:
            allUser.find((user) => user.UserID === userId)?.UserName ||
            item.UserName ||
            '',
          email: userId,
          inviter: item.InviterUserID,
          roles: roles
            .filter((role) => role.userList.includes(userId))
            .map((role) => {
              return { name: role.roleName, type: role.type };
            }),
        };
      })
      .sort((a, b) => {
        const sortMap = [
          RbacRoleType.Custom,
          RbacRoleType.Admin,
          RbacRoleType.Owner,
        ];

        const getMaxRole = (member: IAdminMemberItem) => {
          return member.roles.length
            ? Math.max(
                ...member.roles.map((role) => sortMap.indexOf(role.type))
              )
            : 0;
        };

        const aMaxRole = getMaxRole(a);
        const bMaxRole = getMaxRole(b);

        return aMaxRole < bMaxRole ? 1 : -1;
      });

    setMembers(formatUsers);
  };

  const handleEditUser = (operateUser: IAdminMemberItem) => {
    if (!userInfo) {
      return;
    }
    setEditUser(operateUser);
  };

  const removeUser = (removeUserID: string) => {
    ModalWrap.warn({
      title: getFormatMessage('remove_member'),
      oKText: getFormatMessage('Confirm'),
      cancelText: getFormatMessage('cancel'),
      children: (
        <div style={{ whiteSpace: 'pre-line' }}>
          {getFormatMessage('delete_member_desc', {
            params: {
              member: removeUserID,
            },
          })}
        </div>
      ),
      onOk: async () => {
        const matchedMember = supportMembers.find(
          (item) => item.UserID === removeUserID
        );
        if (!userInfo || !matchedMember) {
          return false;
        }

        const matchedRoles = roles.filter((role) =>
          role.userList.includes(removeUserID)
        );
        let operateRes = true;

        for (const matchedRole of matchedRoles) {
          operateRes = await saveSupportRoleUsers({
            roleId: matchedRole.roleId,
            userIdList: matchedRole.userList.filter(
              (name) => name !== removeUserID
            ),
            operator: userInfo.UserID,
          });
          if (!operateRes) {
            break;
          }
        }

        if (operateRes) {
          operateRes = await deleteSupportUser(matchedMember.id);
        }

        if (!operateRes) {
          messageError(getFormatMessage('operation_failed'));
          return false;
        }

        onChange();
        return true;
      },
    });
  };

  const transfer = (transferUserID: string) => {
    ModalWrap.warn({
      title: getFormatMessage('转让超级管理员'),
      oKText: getFormatMessage('Confirm'),
      cancelText: getFormatMessage('cancel'),
      children: (
        <div style={{ whiteSpace: 'pre-line' }}>
          {getFormatMessage(`确定将超级管理员转让给【${transferUserID}】吗`)}
        </div>
      ),
      onOk: async () => {
        if (!userInfo) {
          return false;
        }

        const ownerRole = roles.find(
          (role) => role.type === RbacRoleType.Owner
        );

        const adminRole = roles.find(
          (role) => role.type === RbacRoleType.Admin
        );
        if (!ownerRole || !adminRole) {
          return false;
        }

        const res = await batchEditSupportRole([
          {
            ...adminRole,
            userList: [
              ...adminRole.userList.filter((name) => name !== transferUserID),
              userInfo.UserID,
            ],
          },
          {
            ...ownerRole,
            userList: ownerRole.userList
              .filter((name) => name !== userInfo.UserID)
              .concat(transferUserID),
          },
        ]);

        if (!res) {
          messageError(getFormatMessage('failed'));
          return false;
        }

        onChange();
        return true;
      },
    });
  };

  const quit = () => {
    ModalWrap.warn({
      title: getFormatMessage('quit_title'),
      oKText: getFormatMessage('Confirm'),
      cancelText: getFormatMessage('cancel'),
      children: getFormatMessage('quit_body'),
      onOk: async () => {
        const matchedMember = supportMembers.find(
          (item) => item.UserID === userInfo?.UserID
        );
        if (!userInfo || !matchedMember) {
          return false;
        }

        let operateRes = false;

        const res = await Promise.all(
          roles
            .filter((roleItem) => roleItem.userList.includes(userInfo.UserID))
            .map(async (roleItem) => {
              return saveSupportRoleUsers({
                operator: userInfo.UserID,
                userIdList: roleItem.userList.filter(
                  (name) => name !== userInfo.UserID
                ),
                roleId: roleItem.roleId,
              });
            })
        );

        operateRes = res.every(Boolean);

        if (operateRes) {
          operateRes = await deleteSupportUser(matchedMember.id);
        }

        if (!operateRes) {
          messageError(getFormatMessage('failed'));
          return false;
        }

        onChange();
        window.location.reload();
        return true;
      },
    });
  };

  const isSuper = members
    .find((member) => member.email === userInfo?.UserID)
    ?.roles.some((role) => role.type === RbacRoleType.Owner);
  const isAdmin = members
    .find((member) => member.email === userInfo?.UserID)
    ?.roles.some((role) => role.type === RbacRoleType.Admin);

  const showInvite = () => {
    setShowInviteDrawer(true);
  };

  return (
    <div className={styles.memberPage}>
      <div>
        <AdminPermCheck hasPerm={isSuper || isAdmin} contentType="action">
          <MainButton
            icon={<SvgIcon icon={SvgIconName.InviteAddIcon} />}
            onClick={showInvite}
          >
            {getFormatMessage('invite_member')}
          </MainButton>
        </AdminPermCheck>
      </div>
      <PaginationTable
        loading={loading}
        list={members}
        defaultPageSize={20}
        configFields={[
          {
            key: 'userName',
            name: getFormatMessage('member_name'),
            tableExtension: {
              renderRow(_key: any, _value: string, row) {
                const isMime = row.email === userInfo?.UserID;
                return (
                  <>
                    <span>{getUserNameByEmail(row.email)}</span>
                    {isMime && <CommonTag type="yellow" text={'YOU'} />}
                  </>
                );
              },
            },
          },
          {
            key: 'email',
            name: getFormatMessage('email_uppercase'),
          },
          {
            key: 'role',
            name: getFormatMessage('role'),
            tableExtension: {
              renderRow(_key: any, _value: string, row) {
                const userItem = row as IAdminMemberItem;
                return (
                  <>
                    {userItem.roles.map((role) => {
                      return <Tag color="geekblue">{role.name}</Tag>;
                    })}
                  </>
                );
              },
            },
          },
          {
            key: 'inviter',
            name: getFormatMessage('邀请人'),
            tableExtension: {
              renderRow(_key: any, _value: string, row) {
                return (
                  <>
                    {row.inviter
                      ? row.inviter.replace('@tencent.com', '')
                      : '系统'}
                  </>
                );
              },
            },
          },
          {
            key: 'action',
            name: getFormatMessage('operate'),
            tableExtension: {
              renderRow(_key: any, _value: string, row) {
                const userItem = row as IAdminMemberItem;
                const rowIsMime = userItem.email === userInfo?.UserID;
                const rowIsAdmin = userItem.roles.some(
                  (role) => role.type === RbacRoleType.Admin
                );
                const rowIsOwner = userItem.roles.some(
                  (role) => role.type === RbacRoleType.Owner
                );

                const rowIsCustom =
                  (userItem.roles.some(
                    (role) => role.type === RbacRoleType.Custom
                  ) ||
                    !userItem.roles.length) &&
                  !rowIsAdmin &&
                  !rowIsOwner;

                const btns = [];

                if (
                  (isSuper && !rowIsMime && !rowIsOwner) ||
                  (isAdmin && !rowIsMime && !rowIsAdmin && !rowIsOwner)
                ) {
                  btns.push(
                    <CommonButton
                      type="link"
                      onClick={() => handleEditUser(userItem)}
                    >
                      {getFormatMessage('edit')}
                    </CommonButton>
                  );
                }

                if (isSuper && rowIsAdmin) {
                  btns.push(
                    <CommonButton
                      type="link"
                      onClick={() => transfer(userItem.email)}
                    >
                      {getFormatMessage('transfer')}
                    </CommonButton>
                  );
                }

                if (rowIsMime && !rowIsOwner) {
                  btns.push(
                    <CommonButton
                      type="link"
                      className="ant-btn-dangerous"
                      onClick={() => quit()}
                    >
                      {getFormatMessage('quit')}
                    </CommonButton>
                  );
                }

                if (
                  isSuper &&
                  getConfig().deployArea === DeployArea.singapore
                ) {
                  btns.push(
                    <CommonButton
                      type="link"
                      onClick={() => {
                        setShowResetPassport(true);
                        setResetPassportMember(userItem);
                      }}
                    >
                      重置密码
                    </CommonButton>
                  );
                }

                if (
                  (isSuper && (rowIsCustom || rowIsAdmin)) ||
                  (isAdmin && rowIsCustom)
                ) {
                  btns.push(
                    <CommonButton
                      type="link"
                      onClick={() => removeUser(userItem.email)}
                      className="ant-btn-dangerous"
                    >
                      {getFormatMessage('remove')}
                    </CommonButton>
                  );
                }

                if (btns.length) {
                  return <CommonButtonGroup>{btns}</CommonButtonGroup>;
                }

                return '--';
              },
            },
          },
        ]}
      />
      {showInviteDrawer && (
        <InviteDrawer
          isSuper={!!isSuper}
          members={members}
          allUser={allUser}
          roles={roles}
          onCancel={() => {
            setShowInviteDrawer(false);
          }}
          onSave={() => {
            setShowInviteDrawer(false);
            onChange();
          }}
        />
      )}
      {!!editUser && userInfo && (
        <EditUserDrawer
          roles={roles}
          operateUser={editUser}
          isSuper={!!isSuper}
          operator={userInfo.UserID}
          onSave={() => {
            setEditUser(undefined);
            onChange();
          }}
          onCancel={() => setEditUser(undefined)}
        />
      )}
      {showResetPassport && (
        <ResetPasswordModal
          sourcePlatform={AppLoginPlatform.Support}
          showResetPasswordModal
          email={resetPassportMember?.email || ''}
          onCancel={() => {
            setShowResetPassport(false);
            setResetPassportMember(undefined);
          }}
        />
      )}
    </div>
  );
};
